import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import invariant from 'invariant';
import { uniq, forEach, o, identity, compose, map, applyTo } from 'ramda';
import { makeStoreInterface, enhanceStore } from '@redux-syringe/injectors';
import { defaultNamespace, getStateByFeatureAndNamespace, DEFAULT_FEATURE, isActionFromNamespace } from '@redux-syringe/namespaces';
var storeInterface = makeStoreInterface('middleware');
var noopEntry = {
  path: ['@redux-syringe/NOOP_MIDDLEWARE'],
  value: function value() {
    return function (next) {
      return function (action) {
        return next(action);
      };
    };
  }
};
var makeEnhancer = function makeEnhancer() {
  // NOTE: Keys are entries, values are middleware with bound `dispatch` and `getState`.
  var initializedEntries = new Map(); // NOTE: Sadly, because of how enhancers and middleware are structured, we need some escape hatches
  // from scopes and closures. This is ugly, but I don't think we can solve this differently.
  // NOTE: `outerNext` is either the next middleware in `applyMiddleware` or `store.dispatch`.

  var outerNext; // NOTE: This default implementation is necessary to ensure that the middleware works even without
  // any injected middleware.
  // NOTE `enhancerNext` calls all injected middleware and then `outerNext`.

  var enhancerNext = function enhancerNext(action) {
    invariant(outerNext, 'You need to apply the enhancer to a Redux store.');
    return outerNext(action);
  };
  var injectedMiddleware = function injectedMiddleware() {
    return function (next) {
      invariant(!outerNext, 'You cannot apply the injected middleware to multiple Redux stores.');
      outerNext = next;
      return function (action) {
        return enhancerNext(action);
      };
    };
  }; // NOTE: composeEntries :: [Entry] -> Next

  var composeEntries = function composeEntries(entries) {
    var chain = map(function (entry) {
      var namespace = entry.namespace; // NOTE: `innerNext` is either the next injected middleware or `outerNext`.

      return function (innerNext) {
        // NOTE: `entryNext` is a wrapper over the currently iterated-over injected middleware.
        var entryNext = initializedEntries.get(entry)(innerNext);
        return function (action) {
          return isActionFromNamespace(namespace, action) ? entryNext(action) : innerNext(action);
        };
      };
    }, entries); // NOTE: `compose` is used to preserve injection order.

    return compose.apply(void 0, _toConsumableArray(chain))(outerNext);
  };
  var enhancer = function enhancer(createStore) {
    return function () {
      var prevStore = createStore.apply(void 0, arguments); // NOTE: All of this logic is just to achieve the following behaviour:
      // Every middleware is curried. In standard Redux, the first two arguments are bound immediately.
      // However, when injecting the middleware, we are not able to easily provide the second argument
      // immediately, because it changes whenever an entry is injected or ejected. That's why we only
      // bind the first argument and then provide `next` once per any injection call. This behaviour
      // is covered by unit tests, which may help explain this better.

      var handleEntriesChanged = function handleEntriesChanged() {
        var nextEntries = [].concat(_toConsumableArray(uniq(storeInterface.getEntries(nextStore))), [
        // NOTE: This is just a safeguard, because although `R.compose` is variadic,
        // it still needs at least one function as an argument.
        noopEntry]);
        var nextInitializedEntries = new Map(); // NOTE: We copy all necessary entries because it's simpler/faster than finding what has changed.

        forEach(function (entry) {
          var _initializedEntries$g;
          var namespace = entry.namespace;
          var dispatch = nextStore.dispatch,
            getState = nextStore.getState;
          nextInitializedEntries.set(entry, (_initializedEntries$g = initializedEntries.get(entry)) !== null && _initializedEntries$g !== void 0 ? _initializedEntries$g : entry.value({
            namespace: namespace,
            dispatch: o(function (action) {
              return dispatch(action);
            }, namespace ? defaultNamespace(namespace) : identity),
            getState: nextStore.getState,
            getNamespacedState: function getNamespacedState(feature) {
              var _ref;
              return namespace ? getStateByFeatureAndNamespace((_ref = feature !== null && feature !== void 0 ? feature : entry.feature) !== null && _ref !== void 0 ? _ref : DEFAULT_FEATURE, namespace, getState()) : undefined;
            }
          }));
        }, nextEntries);
        initializedEntries = nextInitializedEntries;
        enhancerNext = composeEntries(nextEntries);
      };
      var nextStore = enhanceStore(prevStore, storeInterface, {
        onInjected: handleEntriesChanged,
        onEjected: handleEntriesChanged
      });
      return nextStore;
    };
  };
  enhancer.injectedMiddleware = injectedMiddleware;
  return enhancer;
};
var composeMiddleware = function composeMiddleware() {
  for (var _len = arguments.length, middleware = new Array(_len), _key = 0; _key < _len; _key++) {
    middleware[_key] = arguments[_key];
  }
  return function (reduxApi) {
    return function (next) {
      return compose.apply(void 0, _toConsumableArray(map(applyTo(reduxApi), middleware)))(next);
    };
  };
};
export { composeMiddleware, makeEnhancer, storeInterface };